/**
 * Session API endpoints.
 */

import { apiClient } from './client';
import type {
  Session,
  SessionProgress,
  SessionEndSummary,
  SessionOutcome,
} from '@/types';

/**
 * Start a survey session.
 * Called when the player loads to mark the session as started.
 */
export async function startSession(sessionId: string): Promise<Session> {
  const response = await apiClient.post<Session>(
    `/api/v1/player/sessions/${sessionId}/start`
  );
  return response.data;
}

/**
 * Update session progress.
 */
export async function updateProgress(
  sessionId: string,
  progress: Partial<SessionProgress>
): Promise<Session> {
  const response = await apiClient.patch<Session>(
    `/api/v1/player/sessions/${sessionId}/progress`,
    progress
  );
  return response.data;
}

/**
 * Set browser fingerprint for the session.
 */
export async function setFingerprint(
  sessionId: string,
  fingerprint: string
): Promise<void> {
  await apiClient.post(`/api/v1/player/sessions/${sessionId}/fingerprint`, {
    fingerprint,
  });
}

/**
 * Complete the session successfully.
 */
export async function completeSession(
  sessionId: string
): Promise<SessionEndSummary> {
  const response = await apiClient.post<SessionEndSummary>(
    `/api/v1/player/sessions/${sessionId}/complete`
  );
  return response.data;
}

/**
 * Terminate the session with an outcome.
 */
export async function terminateSession(
  sessionId: string,
  outcome: SessionOutcome,
  reason?: string
): Promise<SessionEndSummary> {
  const response = await apiClient.post<SessionEndSummary>(
    `/api/v1/player/sessions/${sessionId}/terminate`,
    { outcome, reason }
  );
  return response.data;
}

/**
 * Get session details.
 */
export async function getSession(sessionId: string): Promise<Session> {
  const response = await apiClient.get<Session>(
    `/api/v1/player/sessions/${sessionId}`
  );
  return response.data;
}
